import React, { useMemo, useState } from 'react';
import { ClipboardCheck, Download, Eye, FilePlus2, Pencil, QrCode, RotateCcw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/Dialog';
import { PageHeader } from '../../components/admin/PageHeader';
import { Panel } from '../../components/Panel';
import { StatusBadge } from '../../components/StatusBadge';
import { notices, noticeStatuses, trafficDivisions } from '../../data/notices';
import { workflowStages } from '../../data/sampleCase';
import type { NoticeRow } from '../../types/notice';
import { toBn } from '../../utils/bangla';
const selectCls = 'h-9 w-full rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1769AA]';
export function NoticeDatabase() {
  const navigate = useNavigate();
  const [f, setF] = useState({
    caseNo: '',
    vehicle: '',
    owner: '',
    mobile: '',
    date: '',
    division: '',
    status: ''
  });
  const [detail, setDetail] = useState<NoticeRow | null>(null);
  const rows = useMemo(() => notices.filter(n => n.caseNo.toLowerCase().includes(f.caseNo.toLowerCase()) && n.vehicle.includes(f.vehicle) && n.owner.includes(f.owner) && n.mobile.includes(f.mobile) && (!f.date || n.date === f.date) && (!f.division || n.division === f.division) && (!f.status || n.approval === f.status)), [f]);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF(s => ({
    ...s,
    [k]: e.target.value
  }));
  const dates = Array.from(new Set(notices.map(n => n.date)));
  return <div data-id="element-1101" data-mp="1refemgvy9h5i">
      <PageHeader screen="WEB SCREEN 03" title="প্রসিকিউশন নোটিশ ব্যবস্থাপনা" subtitle="AI প্রসিকিউশন ব্যবস্থা থেকে প্রাপ্ত ও ডিজিটাইজকৃত নোটিশের ডাটাবেস" actions={<>
            <Button variant="outline" onClick={() => toast.success('Excel ফাইল প্রস্তুত করা হয়েছে (ডেমো)')} data-id="element-1103" data-mp="23f5jutxvhihf">
              <Download className="h-4 w-4" data-id="element-1104" data-mp="28ftc1ixu85d8" /> Excel
            </Button>
            <Button onClick={() => navigate('/admin/upload')} data-id="element-1105" data-mp="2demqpeov22ez">
              <FilePlus2 className="h-4 w-4" data-id="element-1106" data-mp="2dc0h79ietcu4" /> নতুন নোটিশ আপলোড
            </Button>
          </>} data-id="element-1102" data-mp="1rep6yb4gw0ed" />

      <Panel title="অনুসন্ধান" className="mb-6" data-id="element-1107" data-mp="1kpfu4l8yvmwd">
        <div className="grid gap-3 md:grid-cols-4 xl:grid-cols-7" data-id="element-1108" data-mp="1kq8r4ui4iy1b">
          <Field label="মামলা নম্বর" data-id="element-1109" data-mp="1poinh8fl44oi"><Input className="h-9" value={f.caseNo} onChange={set('caseNo')} placeholder="DMP/AI/2025/..." data-id="element-1110" data-mp="1po0lrwa5ky97" /></Field>
          <Field label="গাড়ির নম্বর" data-id="element-1111" data-mp="1ugtoyfpz3yj6"><Input className="h-9" value={f.vehicle} onChange={set('vehicle')} placeholder="ঢাকা মেট্রো-..." data-id="element-1112" data-mp="x04pgsohx8cy" /></Field>
          <Field label="মালিকের নাম" data-id="element-1113" data-mp="11yoc6vawyo6d"><Input className="h-9" value={f.owner} onChange={set('owner')} data-id="element-1114" data-mp="11y68xepu39oy" /></Field>
          <Field label="মোবাইল নম্বর" data-id="element-1115" data-mp="ufylwm3snjwx"><Input className="h-9" value={f.mobile} onChange={set('mobile')} placeholder="০১৮..." data-id="element-1116" data-mp="uednumi9tiub" /></Field>
          <Field label="তারিখ" data-id="element-1117" data-mp="ph55olx4het2">
            <select className={selectCls} value={f.date} onChange={set('date')} data-id="element-1118" data-mp="khit8ep4c2lb">
              <option value="" data-id="element-1119" data-mp="kjltrsnwde4a">সকল</option>
              {dates.map(d => <option key={d} data-id="element-1120" data-mp="fhecqoo7n7v3">{d}</option>)}
            </select>
          </Field>
          <Field label="ট্রাফিক বিভাগ" data-id="element-1121" data-mp="bhcll9g3pwu2">
            <select className={selectCls} value={f.division} onChange={set('division')} data-id="element-1122" data-mp="bidy7icrmrdw">
              <option value="" data-id="element-1123" data-mp="6ojewg12oefc">সকল</option>
              {trafficDivisions.map(d => <option key={d} data-id="element-1124" data-mp="1q8688mik9yp">{d}</option>)}
            </select>
          </Field>
          <Field label="নোটিশের অবস্থা" data-id="element-1125" data-mp="3sxkoys31ovgq">
            <select className={selectCls} value={f.status} onChange={set('status')} data-id="element-1126" data-mp="3syngn286z1xp">
              <option value="" data-id="element-1127" data-mp="3nx6ez4hvvbg2">সকল</option>
              {['অনুমোদিত', ...noticeStatuses.slice(1)].map(d => <option key={d} data-id="element-1128" data-mp="3jxp6e275ty6o">{d}</option>)}
            </select>
          </Field>
        </div>
        <div className="mt-3 flex items-center justify-between text-sm" data-id="element-1129" data-mp="chmiumvrrwi4">
          <span className="text-slate-500" data-id="element-1130" data-mp="hgppucvb8csq">{toBn(rows.length)}টি রেকর্ড পাওয়া গেছে</span>
          <Button variant="ghost" size="sm" onClick={() => setF({
          caseNo: '',
          vehicle: '',
          owner: '',
          mobile: '',
          date: '',
          division: '',
          status: ''
        })} data-id="element-1131" data-mp="3pklwykwsycl">
            <RotateCcw className="h-3.5 w-3.5" data-id="element-1132" data-mp="7puzawb16r1h" /> রিসেট
          </Button>
        </div>
      </Panel>

      <Panel bodyClassName="overflow-x-auto" data-id="element-1133" data-mp="3pv7q3sf0eo1s">
        <table className="w-full min-w-[1100px] text-sm" data-id="element-1134" data-mp="3uvvoiz5ki761">
          <thead className="bg-[#003B71] text-left text-xs text-white" data-id="element-1135" data-mp="3uxgjgpvsns4f">
            <tr data-id="element-1136" data-mp="3gx5vbulvcrmy">
              {['ক্রমিক', 'মামলা নম্বর', 'গাড়ির নম্বর', 'মামলার তারিখ', 'অপরাধের ধরন', 'মালিকের নাম', 'OCR অবস্থা', 'অনুমোদন অবস্থা', 'QR নোটিশ অবস্থা', 'অ্যাকশন'].map(h => <th key={h} className="px-3 py-3 font-medium" data-id="element-1137" data-mp="1f5srl8vn0psh">{h}</th>)}
            </tr>
          </thead>
          <tbody data-id="element-1138" data-mp="1j5t42nz6eqww">
            {rows.map((n, i) => <tr key={n.caseNo} className={`border-t border-border ${i === 0 && n.caseNo.endsWith('0748') ? 'bg-[#E8F1FA]/50' : 'hover:bg-[#F4F7FB]'}`} data-id="element-1139" data-mp="33o3xd9edxxcb" data-mpi={i}>
                <td className="px-3 py-3 text-slate-500" data-id="element-1140" data-mp="2zomt2jap368q">{toBn(i + 1)}</td>
                <td className="px-3 py-3 font-mono text-[12.5px] font-medium text-[#003B71]" data-id="element-1141" data-mp="3df8ae3ki384i">{n.caseNo}</td>
                <td className="px-3 py-3" data-id="element-1142" data-mp="38morcr31nksb">{n.vehicle}</td>
                <td className="px-3 py-3" data-id="element-1143" data-mp="3nc4dtuvjl427">{n.date}</td>
                <td className="px-3 py-3" data-id="element-1144" data-mp="3najiw45bfj3t">{n.offence}</td>
                <td className="px-3 py-3" data-id="element-1145" data-mp="3ie2v5b2136r9">{n.owner}</td>
                <td className="px-3 py-3" data-id="element-1146" data-mp="3ife19hiy4qlj"><StatusBadge label={n.ocr} data-id="element-1147" data-mp="7ep9iz5mfkj" /></td>
                <td className="px-3 py-3" data-id="element-1148" data-mp="75180xo6ihh"><StatusBadge label={n.approval} data-id="element-1149" data-mp="5apnd8ag2q2" /></td>
                <td className="px-3 py-3" data-id="element-1150" data-mp="3rf0xlowtj67q"><StatusBadge label={n.qr} data-id="element-1151" data-mp="3rer7m1brwz21" /></td>
                <td className="px-3 py-3" data-id="element-1152" data-mp="21yi3sjc60ead">
                  <div className="flex gap-1" data-id="element-1153" data-mp="21zjn1b3c4736">
                    <IconBtn label="বিস্তারিত দেখুন" onClick={() => setDetail(n)} data-id="element-1154" data-mp="1x06vid1iqbu9"><Eye className="h-4 w-4" data-id="element-1155" data-mp="1x21evmx55vw8" /></IconBtn>
                    <IconBtn label="সম্পাদনা করুন" onClick={() => navigate('/admin/review')} data-id="element-1156" data-mp="xli430xtzsz"><Pencil className="h-4 w-4" data-id="element-1157" data-mp="5vn3wvpr0d85" /></IconBtn>
                    <IconBtn label="যাচাই করুন" onClick={() => navigate('/admin/validate')} data-id="element-1158" data-mp="5w59iiyaw3sq"><ClipboardCheck className="h-4 w-4" data-id="element-1159" data-mp="atwxx2zql7s6" /></IconBtn>
                    <IconBtn label="QR নোটিশ দেখুন" disabled={n.qr !== 'তৈরি'} onClick={() => navigate('/admin/sample-notice')} data-id="element-1160" data-mp="ewbjhuusiln5"><QrCode className="h-4 w-4" data-id="element-1161" data-mp="jw69lw6qr775" /></IconBtn>
                  </div>
                </td>
              </tr>)}
            {rows.length === 0 && <tr data-id="element-1162" data-mp="osekuyhtig91">
                <td colSpan={10} className="px-3 py-12 text-center text-slate-500" data-id="element-1163" data-mp="tlpv79lu9f5p">প্রদত্ত তথ্যের সাথে কোনো রেকর্ড মেলেনি।</td>
              </tr>}
          </tbody>
        </table>
        <div className="flex items-center justify-between border-t border-border px-4 py-3 text-xs text-slate-500" data-id="element-1164" data-mp="12m5wqdj0uc7x">
          <span data-id="element-1165" data-mp="17kpiodxm7o0a">মোট ২৫,৪৮০টির মধ্যে ১–{toBn(rows.length)} দেখানো হচ্ছে (নমুনা)</span>
          <div className="flex gap-1" data-id="element-1166" data-mp="2s31e6007kbvf">
            {['১', '২', '৩', '…', '২১২৪'].map((p, i) => <span key={p} className={`rounded px-2.5 py-1 ${i === 0 ? 'bg-[#003B71] text-white' : 'border border-border'}`} data-id="element-1167" data-mp="2n4gd5yd4to5x" data-mpi={i}>{p}</span>)}
          </div>
        </div>
      </Panel>

      <Dialog open={!!detail} onOpenChange={o => !o && setDetail(null)} data-id="element-1168" data-mp="2e3i7zcqyjalr">
        <DialogContent className="sm:max-w-2xl" data-id="element-1169" data-mp="2e5l6yma38e2m">
          {detail && <>
              <DialogHeader data-id="element-1170" data-mp="3aszji8cmfqvd">
                <DialogTitle data-id="element-1171" data-mp="36s5ufbng060w">মামলার বিস্তারিত</DialogTitle>
                <DialogDescription className="font-mono" data-id="element-1172" data-mp="36spf304h98im">{detail.caseNo}</DialogDescription>
              </DialogHeader>
              <div className="max-h-[60vh] space-y-4 overflow-y-auto px-6 pb-2" data-id="element-1173" data-mp="31zcqur3pn3qe">
                <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm" data-id="element-1174" data-mp="2x247woaqmvo3">
                  {[['গাড়ির নম্বর', detail.vehicle], ['মামলার তারিখ', detail.date], ['অপরাধের ধরন', detail.offence], ['মালিকের নাম', detail.owner], ['মোবাইল', detail.mobile], ['ট্রাফিক বিভাগ', detail.division]].map(([k, v]) => <div key={k} data-id="element-1175" data-mp="2o5khmlavowq1">
                      <dt className="text-xs text-slate-500" data-id="element-1176" data-mp="2o6lv0wfd9vax">{k}</dt>
                      <dd className="font-medium" data-id="element-1177" data-mp="2t3ui9bfdgpj1">{v}</dd>
                    </div>)}
                </dl>
                <div className="flex flex-wrap gap-2" data-id="element-1178" data-mp="3guzsnb2jmply">
                  <StatusBadge label={`OCR: ${detail.ocr}`} tone="info" data-id="element-1179" data-mp="3lt1c3x3vsqxy" />
                  <StatusBadge label={detail.approval} data-id="element-1180" data-mp="3lqyqdp9rhjz1" />
                  <StatusBadge label={`QR: ${detail.qr}`} tone="neutral" data-id="element-1181" data-mp="37u1sj3v8728n" />
                </div>
                <div data-id="element-1182" data-mp="3bu3rv6dhnjkr">
                  <h3 className="mb-2 text-sm font-semibold text-[#003B71]" data-id="element-1183" data-mp="3btkdfzn1rdbh">প্রক্রিয়াকরণ ইতিহাস</h3>
                  <ol className="space-y-2 border-l-2 border-[#1769AA]/30 pl-4" data-id="element-1184" data-mp="1wcjtuisi4ssz">
                    {workflowStages.slice(0, detail.approval === 'অনুমোদিত' ? 7 : 3).map(s => <li key={s.label} className="text-sm" data-id="element-1185" data-mp="1sv5xgrbagvxj">
                        <span className="font-medium" data-id="element-1186" data-mp="1nx4d8324mqkh">{s.label}</span> — <span className="text-slate-600" data-id="element-1187" data-mp="1nwb3qubs9myn">{s.officer}</span>
                        <div className="text-xs text-slate-500" data-id="element-1188" data-mp="22umz8xnf6po8">{s.at}</div>
                      </li>)}
                  </ol>
                </div>
              </div>
              <DialogFooter data-id="element-1189" data-mp="1a5fzxwg55w1x">
                <Button variant="outline" onClick={() => navigate('/admin/status')} data-id="element-1190" data-mp="1a6heiawd90oe">নোটিশ হালনাগাদ / নতুন সংস্করণ</Button>
                <Button onClick={() => navigate('/admin/sample-notice')} data-id="element-1191" data-mp="158fwltaof7ei">QR নোটিশ দেখুন</Button>
              </DialogFooter>
            </>}
        </DialogContent>
      </Dialog>
    </div>;
}
function Field({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <label className="block" data-id="element-1192" data-mp="rddiiivoebqm">
      <span className="mb-1 block text-xs font-medium text-slate-600" data-id="element-1193" data-mp="mg4w263hvljk">{label}</span>
      {children}
    </label>;
}
function IconBtn({
  label,
  children,
  onClick,
  disabled
}: {
  label: string;
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return <button type="button" title={label} aria-label={label} disabled={disabled} onClick={onClick} className="rounded-md border border-border p-1.5 text-[#003B71] hover:bg-[#E8F1FA] disabled:cursor-not-allowed disabled:opacity-35" data-id="element-1194" data-mp="2s92yzi1aqvbg">
      {children}
    </button>;
}